import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkErrorType } from 'shared/enums/link-error-type';
import { DeviceType } from 'shared/models/device-type';

import LinkableDevice from 'features/device/linkable-device/domain/models/linkable-device';
import { useDeviceLinkContextProvider } from 'features/device/providers/device-link-provider/device-link-provider';
import OrganisationUnitDevice from 'features/organisation/domain/models/organisation-unit-device';

type OrganisationDetailsLinkableDevicesModal = {
  hasDeviceLinkError: LinkErrorType | undefined;
  handleOnClose: () => void;
  handleOnLinkButtonClicked: () => void;
  getErrorMessageForDevice: (device: LinkableDevice) => string | undefined;
};

type Props = {
  onClose: () => void;
  onLinkButtonClicked: (selectedDevices: OrganisationUnitDevice[]) => void;
};

const useOrganisationDetailsLinkableDevicesModal = ({ onClose, onLinkButtonClicked }: Props): OrganisationDetailsLinkableDevicesModal => {
  const { selectedDevices, handleSelectedDevicesChanged, devicesToHide } = useDeviceLinkContextProvider();
  const [hasDeviceLinkError, setHasDeviceLinkError] = useState<LinkErrorType | undefined>(undefined);
  const { t } = useTranslation('organisation', { keyPrefix: 'linkableDevicesModal' });

  const handleOnClose = (): void => {
    onClose();
    handleSelectedDevicesChanged([]);
  };

  const handleOnLinkButtonClicked = (): void => {
    if (hasDeviceLinkError) {
      return;
    }
    onLinkButtonClicked(selectedDevices);
    handleSelectedDevicesChanged([]);
  };

  const getErrorMessageForDevice = (device: LinkableDevice): string | undefined => {
    if (
      hasDeviceLinkError === LinkErrorType.DesoAlreadyLinked &&
      device.type === DeviceType.DesoResidentTransmitter &&
      selectedDevices.some((d) => d.id === device.id)
    ) {
      return t('deviceAlreadyLinkedToOrganisationUnit');
    }
    return undefined;
  };

  const handleOnSelectedDevicesChanged = (devices: LinkableDevice[]): void => {
    const desoDevices = devices.filter((x) => x.type === DeviceType.DesoResidentTransmitter);

    if (desoDevices.length > 1 || (devicesToHide.some((x) => x.type === DeviceType.DesoResidentTransmitter) && desoDevices.length > 0)) {
      setHasDeviceLinkError(LinkErrorType.DesoAlreadyLinked);
    } else {
      setHasDeviceLinkError(undefined);
    }
  };

  useEffect(() => {
    handleOnSelectedDevicesChanged(selectedDevices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevices]);

  return {
    hasDeviceLinkError,
    handleOnClose,
    handleOnLinkButtonClicked,
    getErrorMessageForDevice,
  };
};

export default useOrganisationDetailsLinkableDevicesModal;
